import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import Layout from '../components/layout/Layout';
import Seo from '../components/seo/Seo';
import { theme } from '../styles/theme';
import { sendContactMessage } from '../services/contact/sendContact';
import { formatPhoneNumberInput } from '../utils/phoneFormat';
import {
  WebsiteSection,
  WebsiteSectionContainer,
  WebsitePageHeader,
  WebsitePageTitle,
  WebsitePageIntro,
  WebsiteCard,
  WebsiteFormGroup,
  WebsiteFormLabel,
  WebsiteFormInput,
  WebsiteFormTextarea,
  WebsiteFormAlert,
  WebsitePrimaryButton,
} from '../components/common/websiteSection';

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  gap: ${theme.spacing.xl};
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContactCard = styled(WebsiteCard)`
  transition: none;

  &:hover {
    border-color: var(--login-form-border);
    box-shadow: var(--login-form-shadow);
    transform: none;
  }
`;

const FormCard = styled(ContactCard)`
  padding: ${theme.spacing.xl};
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const SubmitButton = styled(WebsitePrimaryButton)`
  width: 100%;
  margin-top: ${theme.spacing.sm};
`;

const InfoCard = styled(ContactCard)`
  padding: ${theme.spacing.xl};
`;

const InfoItem = styled.div`
  &:not(:last-child) {
    margin-bottom: ${theme.spacing.xl};
    padding-bottom: ${theme.spacing.xl};
    border-bottom: 1px solid var(--login-form-border);
  }

  h3 {
    margin: 0 0 ${theme.spacing.sm};
    font-size: ${theme.typography.fontSize.base};
    font-weight: 700;
    color: var(--login-primary);
  }

  p {
    margin: 0;
    font-size: ${theme.typography.fontSize.base};
    line-height: 1.65;
    color: var(--login-form-muted);
  }
`;

export default function ContactPage() {
  const location = useLocation();
  const contactPrefill = location.state || {};

  const [formData, setFormData] = useState({
    name: contactPrefill.name || '',
    email: contactPrefill.email || '',
    phone: formatPhoneNumberInput(contactPrefill.phone || ''),
    subject: contactPrefill.subject || '',
    message: contactPrefill.message || '',
  });
  const [status, setStatus] = useState({ loading: false, success: null, error: null });

  useEffect(() => {
    if (!contactPrefill.subject && !contactPrefill.message) return;
    setFormData((prev) => ({
      ...prev,
      subject: contactPrefill.subject || prev.subject,
      message: contactPrefill.message || prev.message,
    }));
  }, [contactPrefill.subject, contactPrefill.message]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setStatus({ loading: true, success: null, error: null });
      await sendContactMessage(formData);
      setStatus({
        loading: false,
        success: 'Message sent! We will get back to you shortly.',
        error: null,
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      setStatus({
        loading: false,
        success: null,
        error: 'Failed to send message. Please try again.',
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'phone' ? formatPhoneNumberInput(value) : value,
    });
  };

  return (
    <Layout>
      <Seo
        title="Contact Neat Schedule"
        description="Get in touch with the Neat Schedule team. Send a message or reach us by phone, email, or during business hours."
        canonicalPath="/contact"
        keywords={['contact Neat Schedule', 'Neat Schedule support', 'book a demo']}
      />
      <WebsiteSection>
        <WebsiteSectionContainer>
          <WebsitePageHeader>
            <WebsitePageTitle>Contact Us</WebsitePageTitle>
            <WebsitePageIntro>
              {contactPrefill.planName
                ? `Tell us about your needs for the ${contactPrefill.planName} plan. We will follow up with pricing and next steps.`
                : 'Have questions? We would love to hear from you. Send us a message and we will respond as soon as possible.'}
            </WebsitePageIntro>
          </WebsitePageHeader>

          <ContactGrid>
            <FormCard>
              <ContactForm onSubmit={handleSubmit}>
                <WebsiteFormGroup>
                  <WebsiteFormLabel htmlFor="name">Name</WebsiteFormLabel>
                  <WebsiteFormInput
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </WebsiteFormGroup>

                <WebsiteFormGroup>
                  <WebsiteFormLabel htmlFor="email">Email</WebsiteFormLabel>
                  <WebsiteFormInput
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </WebsiteFormGroup>

                <WebsiteFormGroup>
                  <WebsiteFormLabel htmlFor="phone">Phone</WebsiteFormLabel>
                  <WebsiteFormInput
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 555-5555"
                    autoComplete="tel"
                    inputMode="tel"
                    required
                  />
                </WebsiteFormGroup>

                <WebsiteFormGroup>
                  <WebsiteFormLabel htmlFor="subject">Subject</WebsiteFormLabel>
                  <WebsiteFormInput
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  />
                </WebsiteFormGroup>

                <WebsiteFormGroup>
                  <WebsiteFormLabel htmlFor="message">Message</WebsiteFormLabel>
                  <WebsiteFormTextarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </WebsiteFormGroup>

                <SubmitButton type="submit" disabled={status.loading}>
                  {status.loading ? 'Sending…' : 'Send Message'}
                </SubmitButton>

                {status.success && (
                  <WebsiteFormAlert $variant="success">{status.success}</WebsiteFormAlert>
                )}
                {status.error && (
                  <WebsiteFormAlert $variant="error">{status.error}</WebsiteFormAlert>
                )}
              </ContactForm>
            </FormCard>

            <InfoCard>
              <InfoItem>
                <h3>Phone</h3>
                <p>+1 (806) 341-9918</p>
              </InfoItem>
              <InfoItem>
                <h3>Email</h3>
                <p>contact@neatschedule.com</p>
              </InfoItem>
              <InfoItem>
                <h3>Business Hours</h3>
                <p>Monday – Friday: 9:00 AM – 5:00 PM CST</p>
              </InfoItem>
              <InfoItem>
                <h3>Address</h3>
                <p>3108 34th St</p>
                <p>Lubbock, TX 79410</p>
              </InfoItem>
            </InfoCard>
          </ContactGrid>
        </WebsiteSectionContainer>
      </WebsiteSection>
    </Layout>
  );
}
